import React, { useState } from "react";

import { ZonedDateTime } from "@js-joda/core";
import { MdmObjectDto, MdmObjectJobDto, useDeleteJobMutation, useGetJobsByObjectIdQuery, useGetJobsByProjectIdQuery, useGetObjectQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Link, useParams } from "react-router-dom";
import { TableColumn } from "utils/types";
import { statusType } from "../utils/const";
import { errorToaster, renderSeverity, severityRowFilterTemplate, successToaster } from "../utils/utils";
import ItemsTable from "./ItemsTable";
import JobForm from "./JobForm";

type ObjectJobsProps = {
  object?: MdmObjectDto;
  projectId?: string;
  showToolbarBtn?: boolean;
}

const ObjectJobs: React.FC<ObjectJobsProps> = ({ object, projectId, showToolbarBtn = true }) => {
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<MdmObjectJobDto| null>(null);

  const { data, loading } = useGetJobsByObjectIdQuery({
    variables: {
      id: object?.id!
    },
    skip: !object?.id
  });

  const { data: jobsData, loading: jobsLoading } = useGetJobsByProjectIdQuery({
    variables: {
      id: projectId!
    },
    skip: !projectId
  });

  const jobs: MdmObjectJobDto[] = data?.allJobsByObjectId ?? jobsData?.allJobsByProjectId ?? [];
  const isLoading = loading || jobsLoading;

  const [deleteJobMutation] = useDeleteJobMutation({
    onCompleted: () => successToaster("Job deleted successfully"),
    onError: errorToaster,
  });

  const renderTag = (rowData: MdmObjectJobDto) => <Tag value={statusType[rowData?.status!].value} severity={statusType[rowData?.status!].severity} />

  const statusRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={Object.entries(statusType).map(([key, value]) => ({ label: value.value, value: key }))}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        showClear
      />
    );
  };

  const renderActions = (rowData: MdmObjectJobDto) => (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text h-2rem w-2rem"
        onClick={() => handleEditClick(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger h-2rem w-2rem"
        onClick={e => handleDeleteJob(rowData, e)}
      />
    </>
  );

  const handleEditClick = (rowData: MdmObjectJobDto) => {
    setJob(rowData);
    setOpen(true);
  }

  const handleDeleteJob = (jobData: MdmObjectJobDto, event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event?.currentTarget,
      message: "Are you sure you want to delete this job?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteJobMutation({
          variables: {
            jobId: jobData.id,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: jobData.id, __typename: "MdmObjectJobDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });
      },
    });
  };

  const getObject = (rowData: MdmObjectJobDto ) => <ObjectLink objectId={rowData?.objectId!} />

  const columns: TableColumn<MdmObjectJobDto>[] = [
    {
      field: "startDate",
      header: "Start Date",
      body: rowData => ZonedDateTime.parse(rowData?.startDate).toLocalDate().toString(),
      sortable: true,
    },
    {
      field: "endDate",
      header: "End Date",
      body: rowData => ZonedDateTime.parse(rowData?.endDate).toLocalDate().toString(),
      sortable: true,
    },
    { field: "createdBy", header: "Created by", filter: true },
    {
      field: "severity",
      header: "Priority",
      filter: true,
      body: renderSeverity,
      filterElement: severityRowFilterTemplate,
      filterMatchMode: "equals",
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
    },
    {
      field: "status",
      header: "Status",
      body: renderTag,
      sortable: true,
      filter: true,
      filterElement: statusRowFilterTemplate,
      filterMatchMode: "equals",
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
    },
    { field: "title", header: "Title" },
    {
      field: "description",
      header: "Description",
      style: { maxWidth: "32rem" },
    },
    projectId
      ? { field: "objectId", header: "Object", body: getObject }
      : { header: "Actions", body: renderActions, hideInFile: true },
  ]; 

  const handleCloseDialog = () => {
    setJob(null);
    setOpen(false);
  }
  
  return (
    <>
      <ConfirmPopup />
      <ItemsTable
        data={jobs}
        isLoading={isLoading}
        showToolbarBtn={showToolbarBtn}
        columns={columns}
        tableName="Jobs"
        onToolbarBtnClick={() => setOpen(true)}
        searchFields={["description", "createdBy", "startDate", "endDate", "title"]}
      />
      <Dialog
        header={job ? "Edit Job" : "Create Job"}
        visible={open}
        onHide={handleCloseDialog}
        className="w-6"
      >
        <JobForm object={object!} closeDialog={handleCloseDialog} job={job} />
      </Dialog>
    </>
  );
}

const ObjectLink: React.FC<{ objectId: string}> = ({ objectId }) => {
  const { projectId } = useParams();
  const {data} = useGetObjectQuery({ variables: { id: objectId! } });
  
  return <Link to={`/mdm/${projectId}/objects/${objectId}`}>{data?.mdmObjectById.title}</Link>;
}

export default ObjectJobs;